/** DOM ids used for auto-scroll between flow steps. */
export const STUDIO_PRESETS_ANCHOR_ID = "studio-presets";
export const STUDIO_CONFIG_ANCHOR_ID = "studio-config";

const CDN_BASE = "https://cdn.todowith.ai/generated/2ae2c1db-53d0-4920-9cf4-a6b5a96db17b";

/** Static asset URLs for the hero before/after example (served from R2 + CDN).
 * Sources for the 4 after variations live in `public/product_studio_flow/`
 * locally and are uploaded to R2 by `server/scripts/upload-static-presets.ts`. */
export const STUDIO_EXAMPLE_BEFORE = `${CDN_BASE}/hero-studio-before.webp`;
export const STUDIO_EXAMPLE_AFTERS = [
  `${CDN_BASE}/hero-studio-after-1.webp`,
  `${CDN_BASE}/hero-studio-after-2.webp`,
  `${CDN_BASE}/hero-studio-after-3.webp`,
  `${CDN_BASE}/hero-studio-after-4.webp`,
] as const;

/** Sentinel id for the "advanced / custom" card in the preset grid — opens the
 * advanced controls panel instead of selecting a creative preset. */
export const STUDIO_ADVANCED_PRESET_ID = "advanced" as const;

/** Visual style preset definitions for the studio flow.
 *
 * `prompt` is the English instruction sent to the AI when this preset is
 * picked (mirrors the auto-prompt path in the existing pipeline — only sent
 * when not in advanced/custom mode).
 *
 * `nameKey` and `descKey` resolve against `t.productCatalog.studio.presets.*`
 * so the user sees localized labels.
 */
export interface StudioPreset {
  id: string;
  nameKey: string;
  descKey: string;
  prompt: string;
  previewImage: string;
  /** True when this preset is shown by default in the grid. Non-featured
   * presets are hidden behind a "More styles" expand toggle. */
  featured: boolean;
}

/** Quick-pick background ideas shown as chips inside the custom-background
 * panel. Each `key` resolves against `t.productStudio.quickBackgrounds.*`. */
export interface StudioBackgroundIdea {
  key: string;
  emoji: string;
}

export const STUDIO_BACKGROUND_QUICK_IDEAS: StudioBackgroundIdea[] = [
  { key: "marble", emoji: "🏛️" },
  { key: "wood", emoji: "🪵" },
  { key: "beach", emoji: "🏖️" },
  { key: "modernLiving", emoji: "🛋️" },
  { key: "cafe", emoji: "☕" },
  { key: "kitchen", emoji: "🍳" },
];

export const STUDIO_PRESETS: StudioPreset[] = [
  // ── Featured (shown by default) ────────────────────────────
  {
    id: "clean-white",
    nameKey: "cleanWhiteName",
    descKey: "cleanWhiteDesc",
    previewImage: `${CDN_BASE}/preset-studio-clean-white.webp`,
    featured: true,
    prompt:
      "Place the product on a clean white seamless studio backdrop with soft, even lighting. No harsh shadows, no patterns, perfect commercial product photography style. Sharp focus on the product, neutral white-balanced color, high resolution, magazine-quality finish.",
  },
  {
    id: "dramatic-light",
    nameKey: "dramaticLightName",
    descKey: "dramaticLightDesc",
    previewImage: `${CDN_BASE}/preset-studio-dramatic-light.webp`,
    featured: true,
    prompt:
      "Dramatic studio lighting with a single soft key light at 45° front-side, deep moody shadows, gradient dark backdrop. Editorial product photography, cinematic atmosphere, rich contrast, premium feel.",
  },
  {
    id: "minimalist",
    nameKey: "minimalistName",
    descKey: "minimalistDesc",
    previewImage: `${CDN_BASE}/preset-studio-minimalist.webp`,
    featured: true,
    prompt:
      "Minimalist composition: neutral beige, sand, or warm grey backdrop, soft natural daylight, generous negative space, clean modern lines. Subtle texture, premium understated aesthetic, magazine product styling.",
  },
  // ── Secondary (revealed via "More styles" toggle) ──────────
  {
    id: "black-white",
    nameKey: "blackWhiteName",
    descKey: "blackWhiteDesc",
    previewImage: `${CDN_BASE}/preset-studio-black-white.webp`,
    featured: false,
    prompt:
      "Black and white product photography, high contrast monochrome, dramatic chiaroscuro lighting, timeless editorial composition. Sharp tonal range, deep blacks, clean whites, premium classic product styling.",
  },
  {
    id: "lifestyle",
    nameKey: "lifestyleName",
    descKey: "lifestyleDesc",
    previewImage: `${CDN_BASE}/preset-studio-lifestyle.webp`,
    featured: false,
    prompt:
      "Lifestyle product photography: place the product in a relevant real-world context with natural environment and warm golden-hour lighting. Authentic atmosphere, shallow depth of field, soft bokeh on background, magazine campaign feel.",
  },
];
